import * as Yup from 'yup';
import CircularProgress from '@material-ui/core/CircularProgress';
import * as React from 'react';
import InputAdornment from '@material-ui/core/InputAdornment';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { Autocomplete, Radios, TextField } from 'mui-rff';
import { getData, overwrite } from 'country-list';
import { useStyles } from './css';
import { AppFragment, AppStatus, AppVisibilityType } from '../../../types/generated';
import { FormPage } from '../../../components/FormPage';
import { FormSection } from '../../../components/FormSection';
import { FormCondition } from '../../../components/FormCondition';
import { AppFragmentCreate } from '../AppCreateForm/AppCreateForm';

overwrite([
    {
        code: 'TW',
        name: 'Taiwan',
    },
]);
const countries = getData();

export const AppFormView: React.FC<{
    app: AppFragment | AppFragmentCreate;
    loading: boolean;
    onSubmit: (values, form, callback) => void;
    deleting?: boolean;
    onDelete?: () => void;
    onCancel: () => void;
}> = ({ app, loading, onSubmit, onCancel, deleting, onDelete }) => {
    const classes: any = useStyles();
    if (app === undefined) {
        return <CircularProgress />;
    }

    const appTypes = [
        { value: 'PUBLIC', label: 'Public app' },
        { value: 'CUSTOM', label: 'Custom app' },
    ];

    const chargeTypes = [
        { value: 'FREE_TO_INSTALL', label: 'Free to install' },
        { value: 'RECURRING', label: 'Recurring charge' },
        { value: 'ONE_TIME', label: 'One time charge' },
    ];

    const breadcrumbs = [{ label: 'Apps', link: '/extranet/apps' }];
    if (app.id) {
        breadcrumbs.push({
            label: 'Dashboard',
            link: '/extranet/apps/' + app.id + '/dashboard',
        });
    }

    const optionalUrl = Yup.string()
        .matches(
            /^(?:([a-z0-9+.-]+):\/\/)(?:\S+(?::\S*)?@)?(?:(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*\.?)(?::\d{2,5})?(?:[/?#]\S*)?$/,
            { excludeEmptyString: true, message: 'Must be a valid URL' }
        )
        .nullable()
        .max(255, 'Max 255 chars');

    const validationSchema = Yup.object().shape({
        name: Yup.string().required('Name is required').max(30, 'Max 30 chars'),
        appUrl: optionalUrl,
        redirectUrlWhitelist: Yup.string().max(1000, 'Max 1000 chars'),
        apiContactEmail: Yup.string()
            .email('API Contact Email is not a valid email address')
            .required('API Contact Email is required'),
        type: Yup.string().required('You must select app type'),
        chargeType: Yup.string().when('type', {
            is: 'PUBLIC',
            then: Yup.string().required('You must select a charge type'),
        }),
        oneTimeChargeAmount: Yup.number().when(['type', 'chargeType'], {
            is: (type, chargeType) => type === 'PUBLIC' && chargeType === 'ONE_TIME',
            then: Yup.number()
                .required('Must enter the one time charge amount')
                .moreThan(0, 'Must be larger than zero'),
        }),
        freeTrialDays: Yup.number().when(['type', 'chargeType'], {
            is: (type, chargeType) => type === 'PUBLIC' && chargeType === 'RECURRING',
            then: Yup.number()
                .required('Must enter the free trial days')
                .min(0, 'Must not be a negative number'),
        }),
    });

    const canBeDeleted =
        (app.type === AppVisibilityType.Public &&
            (app.status === AppStatus.Draft ||
                app.status === AppStatus.Rejected ||
                app.status === AppStatus.Unpublished)) ||
        (app.type === AppVisibilityType.Custom && !app.linkedVendor);

    return (
        <FormPage
            title={app.id ? 'Edit App Setup' : 'Create App'}
            loading={loading}
            onCancel={onCancel}
            initialValues={app}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            onDelete={canBeDeleted ? onDelete : undefined}
            deleting={deleting}
            breadcrumbs={breadcrumbs}
            render={() => (
                <React.Fragment>
                    {!app.id && (
                        <FormSection
                            title="App Type"
                            description="Are you developing a custom app for a single vendor, or a public app for the app store?"
                        >
                            <div>
                                <Radios
                                    name="type"
                                    data={appTypes}
                                    radioGroupProps={{
                                        classes: classes,
                                        //@ts-ignore
                                        margin: 'normal',
                                        fullWidth: true,
                                    }}
                                />
                            </div>

                            <FormCondition when="type" is="CUSTOM">
                                <Paper variant="outlined" className={classes.typeDesc}>
                                    <Typography>
                                        An app custom built for a single client.
                                    </Typography>
                                    <ul>
                                        <li>Limited to use by one vendor only</li>
                                        <li>Not reviewed by Bókun</li>
                                        <li>Can't use the billing support to charge vendors</li>
                                    </ul>
                                </Paper>
                            </FormCondition>

                            <FormCondition when="type" is="PUBLIC">
                                <Paper variant="outlined" className={classes.typeDesc}>
                                    <Typography>
                                        An app built for a wide vendor audience.
                                    </Typography>
                                    <ul>
                                        <li>Unlimited vendor installs</li>
                                        <li>Reviewed by Bókun to protect trust and security</li>
                                        <li>Listed in the Bókun App Store</li>
                                    </ul>
                                </Paper>
                            </FormCondition>
                        </FormSection>
                    )}

                    <FormSection title="App Info">
                        <TextField
                            name="name"
                            required
                            fullWidth
                            label="Name"
                            margin="normal"
                            autoFocus
                        />
                        <TextField
                            name="apiContactEmail"
                            required
                            fullWidth
                            label="API Contact Email"
                            helperText="We'll use this email to contact you about API issues (such as Webhook failures)."
                            margin="normal"
                        />
                    </FormSection>

                    <FormSection
                        title="URLs"
                        description="Bókun vendors access your app through the URLs you specify here."
                    >
                        <TextField
                            name="appUrl"
                            fullWidth
                            label="App URL"
                            helperText="Vendors will use this URL to access your app. Also, new vendors that want to install your app will be sent to initiate the OAuth installation process."
                            margin="normal"
                        />
                        <TextField
                            name="redirectUrlWhitelist"
                            fullWidth
                            label="Whitelisted redirection URL(s)"
                            helperText="URLs where the user can be redirected after authorizing their Bokun account during installation. You must include at least one redirect URL before making your app public."
                            multiline
                            margin="normal"
                        />
                    </FormSection>

                    <FormSection
                        title="Pricing"
                        description="Describe how you want to charge vendors for using the app."
                    >
                        <div>
                            <Radios
                                name="chargeType"
                                label="How should Bokun charge a vendor for installing your app?"
                                data={chargeTypes}
                                required
                                formControlProps={{
                                    margin: 'normal',
                                }}
                                radioGroupProps={{
                                    //@ts-ignore
                                    classes: { classes },
                                    fullWidth: true,
                                }}
                            />
                        </div>

                        <FormCondition when="chargeType" is="ONE_TIME">
                            <TextField
                                required
                                fullWidth
                                name="oneTimeChargeAmount"
                                label="Charge in USD, billed at time of installation"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">$</InputAdornment>
                                    ),
                                }}
                                margin="normal"
                            />
                        </FormCondition>

                        <FormCondition when="chargeType" is="RECURRING">
                            <TextField
                                required
                                fullWidth
                                name="freeTrialDays"
                                label="How many days of free trial do you offer?"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">days</InputAdornment>
                                    ),
                                }}
                                margin="normal"
                            />
                        </FormCondition>
                    </FormSection>

                    <FormCondition when="type" is="PUBLIC">
                        <FormSection
                            title="Geographic Limitations"
                            description="If your app only works in specific geographic regions, specify the list of countries that are supported here. By default, your app is enabled for all countries."
                        >
                            <Autocomplete
                                name="installationCountries"
                                label="Supported countries"
                                helperText="Leave empty if you support all countries."
                                textFieldProps={{ margin: 'normal' }}
                                options={countries}
                                getOptionValue={(option: { code: string; name: string }) =>
                                    option.code
                                }
                                getOptionLabel={(option: { code: string; name: string }) =>
                                    option.name
                                }
                                disableCloseOnSelect
                                multiple
                            />
                        </FormSection>

                        <FormSection
                            title="Review instructions"
                            description="Provide our App QA specialist team with instructions, credentials, and other important pieces of information so they can try out your app and complete their review. Be sure to include credentials for all accounts they need."
                        >
                            <TextField
                                name="appReviewInstructions"
                                fullWidth
                                label="App review instructions"
                                multiline
                                rows={4}
                                margin="normal"
                            />
                        </FormSection>
                    </FormCondition>
                </React.Fragment>
            )}
        />
    );
};
